import React, { useState, useEffect, useRef } from 'react';
import DataTable from './DataTable';
import { getTestCases, deleteTestCase, getQuestions } from '../../lib/adminApi';

function AdminTestCases() {
  const [testCases, setTestCases] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filterQuestionId, setFilterQuestionId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const isInitialMount = useRef(true);
  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchTestCases = async (page = 1, questionIdFilter = filterQuestionId) => {
    setLoading(true);
    try {
      const questionId = questionIdFilter || null;
      const data = await getTestCases(page, pagination.limit, questionId);
      setTestCases(data.testCases || []);
      setPagination({ page: data.page, limit: data.limit, total: data.total });
    } catch (err) {
      console.error('Failed to fetch test cases:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - only fetch test cases (no need for questions dropdown initially)
  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchTestCases();
  }, []);

  // Fetch questions for dropdown only when user interacts with filter
  const loadQuestionsOnce = useRef(false);
  const handleFilterFocus = async () => {
    if (!loadQuestionsOnce.current) {
      loadQuestionsOnce.current = true;
      try {
        const data = await getQuestions(1, 100);
        setQuestions(data.questions || []);
      } catch (err) {
        console.error('Failed to fetch questions:', err);
      }
    }
  };

  // Fetch test cases when filter changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchTestCases(1);
  }, [filterQuestionId]);

  const handleDelete = async (testCaseId) => {
    try {
      await deleteTestCase(testCaseId);
      setDeleteConfirm(null);
      fetchTestCases(pagination.page);
    } catch (err) {
      console.error('Failed to delete test case:', err);
    }
  };

  const columns = [
    { key: 'testCaseId', label: 'ID' },
    { key: 'questionId', label: 'Question ID' },
    {
      key: 'inputValue',
      label: 'Input',
      render: (val) => (
        <code className="text-xs bg-gray-700 px-2 py-1 rounded">
          {val?.substring(0, 50)}{val?.length > 50 ? '...' : ''}
        </code>
      ),
    },
    {
      key: 'outputValue',
      label: 'Expected Output',
      render: (val) => (
        <code className="text-xs bg-gray-700 px-2 py-1 rounded">
          {val?.substring(0, 50)}{val?.length > 50 ? '...' : ''}
        </code>
      ),
    },
    {
      key: 'hidden',
      label: 'Hidden',
      render: (val) => (
        <span className={`px-2 py-1 rounded text-xs ${val ? 'bg-yellow-600/20 text-yellow-400' : 'bg-gray-600/20 text-gray-400'}`}>
          {val ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  const actions = (row) => (
    <div className="flex gap-2 justify-end">
      <button
        onClick={() => setDeleteConfirm(row)}
        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
      >
        Delete
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Test Cases</h1>
        <div className="flex gap-4">
          <select
            value={filterQuestionId}
            onChange={(e) => setFilterQuestionId(e.target.value)}
            onFocus={handleFilterFocus}
            className="px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
          >
            <option value="">All Questions</option>
            {questions.map((q) => (
              <option key={q.questionId} value={q.questionId}>
                Q#{q.questionId}: {q.question?.substring(0, 30)}...
              </option>
            ))}
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={testCases}
        loading={loading}
        pagination={pagination}
        onPageChange={fetchTestCases}
        actions={actions}
        emptyMessage="No test cases found."
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Delete</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete test case #{deleteConfirm.testCaseId}?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.testCaseId)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTestCases;
